/**
 * Medical reference data — additional Obstetric (OB/GYN) scenarios.
 *
 * High-yield obstetric anaesthesia emergencies not already covered by the core
 * OB scenarios. Each carries monitor physiology plus cognitive-aid (`aid`) and
 * investigation (`investigations`) cards the instructor can push to the learner
 * monitor — same mechanism as the Emergency Manual events.
 *
 * Teaching values, condensed from standard obstetric-anaesthesia practice
 * (SOAP/OAA/ACOG) — adapt to your case and local protocol.
 */
export const OB_SCENARIOS = {
  obSeizure: {
    name: 'Eclamptic Seizure',
    description: 'Generalised tonic-clonic seizure in pre-eclampsia — apnoea/hypoxaemia + hypertensive surge. Protect airway; MgSO₄ first-line.',
    vitals: { HR: 132, SpO2: 84, NIBP_Sys: 192, NIBP_Dia: 116, ABP_Sys: 190, ABP_Dia: 114, EtCO2: 52, RR: 4, Temp: 37.6, CVP: 7 },
    aid: [
      { title: 'Immediate', lines: [
        'Call for help; note seizure time/duration',
        'Protect airway: left lateral, suction, 100% O₂; prevent injury/aspiration',
        'Do NOT force anything into the mouth; do not restrain',
        'Left uterine displacement; prepare airway equipment',
      ] },
      { title: 'Terminate Seizure', lines: [
        'MgSO₄ 4–6 g IV over 15–20 min, then 1–2 g/hr (first-line)',
        'Recurrent seizure: further MgSO₄ 2 g IV',
        'Refractory: midazolam / lorazepam (or short-acting agent)',
        'Intubate if prolonged, airway compromise, or aspiration',
      ] },
      { title: 'After Seizure', lines: [
        'Control BP (target SBP < 160): labetalol or hydralazine',
        'Continuous fetal monitoring; plan delivery once stabilised',
        'Watch for magnesium toxicity → calcium gluconate ready',
        'Rule out other causes if atypical (hypoglycaemia, ICH, hyponatraemia)',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'Generalised tonic-clonic activity → post-ictal drowsiness',
        'Hyperreflexia, clonus; hypertension; tongue-biting, incontinence',
        'Post-ictal hypoventilation and hypoxaemia',
      ] },
      { title: 'Labs', lines: [
        'Pre-eclampsia workup: proteinuria, uric acid ↑',
        'LFTs, platelets, LDH (rule out HELLP)',
        'Glucose, sodium; ABG (post-ictal CO₂ retention)',
      ] },
      { title: 'Fetal / Imaging', lines: [
        'Fetal bradycardia / decelerations during the maternal seizure',
        'Neuroimaging (CT/MRI) only if focal signs or atypical for eclampsia',
      ] },
    ],
  },

  obUterineAtony: {
    name: 'Uterine Atony (PPH)',
    description: 'Postpartum haemorrhage from a boggy, poorly-contracted uterus — the commonest cause of PPH.',
    vitals: { HR: 130, SpO2: 96, NIBP_Sys: 82, NIBP_Dia: 48, ABP_Sys: 80, ABP_Dia: 46, EtCO2: 30, RR: 24, Temp: 36.6, CVP: 2 },
    aid: [
      { title: 'Immediate', lines: [
        'Call for help; alert OB, anaesthesia, nursing; activate MTP if severe',
        'Bimanual uterine massage; empty bladder',
        '100% O₂; large-bore IV ×2; rapid crystalloid then blood',
        'Ensure no retained products / lacerations (cause of "atony")',
      ] },
      { title: 'Uterotonic Ladder', lines: [
        'Oxytocin 40 U in 500 mL infusion (avoid rapid push — hypotension)',
        'Methylergonovine 0.2 mg IM (AVOID if hypertensive)',
        'Carboprost (Hemabate) 250 mcg IM q15min (AVOID if asthma)',
        'Misoprostol 800–1000 mcg PR/SL',
        'Tranexamic acid 1 g IV over 10 min',
      ] },
      { title: 'If Refractory', lines: [
        'Intrauterine balloon tamponade (Bakri)',
        'B-Lynch compression suture; uterine artery ligation',
        'Interventional radiology uterine artery embolisation',
        'Hysterectomy if life-threatening',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'Soft, boggy, poorly-contracted uterus above umbilicus',
        'Brisk vaginal bleeding / clots',
        'Tachycardia, hypotension, pallor',
      ] },
      { title: 'Labs / TEG', lines: [
        'Hgb 7.0 g/dL and falling; platelets 90 K',
        'Fibrinogen 140 mg/dL (target > 200 peripartum)',
        'INR 1.4; lactate 4; iCa low (dilutional/transfusion)',
      ] },
    ],
  },

  obUterineInversion: {
    name: 'Uterine Inversion',
    description: 'Fundus prolapses through the cervix — sudden haemorrhage plus profound vagal bradycardia and shock.',
    vitals: { HR: 44, SpO2: 95, NIBP_Sys: 70, NIBP_Dia: 40, ABP_Sys: 68, ABP_Dia: 38, EtCO2: 30, RR: 20, Temp: 36.6, CVP: 2 },
    aid: [
      { title: 'Immediate', lines: [
        'Call for help (OB, anaesthesia); this is an emergency',
        'STOP all uterotonics (oxytocin/ergot)',
        '100% O₂; large-bore IV ×2; aggressive fluid + blood resuscitation',
        'Treat vagal bradycardia: atropine 0.5–1 mg IV',
      ] },
      { title: 'Replace the Uterus', lines: [
        'Manual replacement (Johnson manoeuvre) — push fundus up',
        'Uterine relaxation to allow replacement:',
        'nitroglycerin 50–100 mcg IV, or terbutaline, or volatile/GA',
        'Once replaced: STOP relaxant, RESTART uterotonics',
        'If manual fails: surgical replacement',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'Smooth mass at introitus / in vagina',
        'Uterine fundus NOT palpable abdominally',
        'Profound bradycardia + hypotension (vagal) with haemorrhage',
      ] },
      { title: 'Labs', lines: [
        'Hgb falling; send coags, fibrinogen; type & cross',
        'Lactate rising with hypoperfusion',
      ] },
    ],
  },

  obMagnesiumToxicity: {
    name: 'Magnesium Toxicity',
    description: 'Iatrogenic hypermagnesaemia during MgSO₄ therapy for pre-eclampsia — CNS/respiratory/cardiac depression.',
    // STOP the magnesium infusion; IV calcium is the antidote; support airway;
    // fluids + furosemide enhance renal excretion.
    correctInterventions: ['calciumChloride', 'intubation', 'ivFluid', 'furosemide'],
    vitals: { HR: 52, SpO2: 92, NIBP_Sys: 96, NIBP_Dia: 56, ABP_Sys: 94, ABP_Dia: 54, EtCO2: 48, RR: 8, Temp: 36.8, CVP: 6 },
    aid: [
      { title: 'Immediate', lines: [
        'STOP the magnesium infusion',
        'Call for help; 100% O₂; support ventilation (may need BMV/intubation)',
        'Calcium gluconate 1 g IV (or calcium chloride) slowly — antagonist',
        'If cardiac arrest: ACLS, continue calcium, consider dialysis',
      ] },
      { title: 'Ongoing', lines: [
        'IV fluids and loop diuretic to enhance Mg excretion',
        'Continuous ECG, SpO₂, respiratory monitoring',
        'Recheck serum Mg and reflexes; reassess seizure prophylaxis need',
        'Consider haemodialysis if renal failure / severe toxicity',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'Loss of deep tendon reflexes (earliest sign, ~10 mEq/L)',
        'Somnolence, flushing, diplopia, slurred speech',
        'Respiratory depression → apnoea; then cardiac arrest',
      ] },
      { title: 'Serum Magnesium', lines: [
        'Mg 11 mEq/L (therapeutic 4–7; DTRs lost > 10; apnoea > 12)',
        'Also send iCa (often low), K⁺, renal function',
      ] },
      { title: 'ECG', lines: [
        'Prolonged PR and QRS; widened QT',
        'Bradyarrhythmia → complete heart block / asystole if severe',
      ] },
    ],
  },

  obHELLP: {
    name: 'HELLP Syndrome',
    description: 'Haemolysis, Elevated Liver enzymes, Low Platelets — severe pre-eclampsia variant with RUQ pain.',
    vitals: { HR: 96, SpO2: 96, NIBP_Sys: 178, NIBP_Dia: 108, ABP_Sys: 176, ABP_Dia: 106, EtCO2: 34, RR: 18, Temp: 37.0, CVP: 7 },
    aid: [
      { title: 'Immediate', lines: [
        'MgSO₄ 4–6 g IV load then 1–2 g/hr (seizure prophylaxis)',
        'Control BP (target SBP < 160): labetalol or hydralazine',
        '100% O₂; large-bore IV; strict I/O, left uterine displacement',
        'Definitive treatment is delivery',
      ] },
      { title: 'Coagulation / Anaesthesia', lines: [
        'Transfuse platelets if < 50 K (or < 80 K for neuraxial/surgery)',
        'FFP / cryoprecipitate for coagulopathy',
        'Neuraxial risky with low platelets → often GA for C-section',
        'Corticosteroids may transiently raise platelets (controversial)',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'RUQ / epigastric tenderness (hepatic capsule distension)',
        'Hypertension, oedema, hyperreflexia, headache, visual changes',
        'Beware subcapsular hepatic haematoma → sudden shock',
      ] },
      { title: 'Labs', lines: [
        'Platelets 45 K; schistocytes on smear (haemolysis)',
        'LDH 820 U/L; AST 190, ALT 165; indirect bilirubin ↑',
        'Haptoglobin low; proteinuria; uric acid ↑; DIC panel',
      ] },
    ],
  },

  obMaternalSepsis: {
    name: 'Maternal Sepsis',
    description: 'Chorioamnionitis / puerperal sepsis progressing to septic shock — warm, vasodilated shock.',
    vitals: { HR: 130, SpO2: 94, NIBP_Sys: 82, NIBP_Dia: 44, ABP_Sys: 80, ABP_Dia: 42, EtCO2: 28, RR: 26, Temp: 39.5, CVP: 3 },
    aid: [
      { title: 'Sepsis Six (< 1 hr)', lines: [
        'Blood cultures (+ genital/urine); measure lactate',
        'Broad-spectrum IV antibiotics within 1 hour',
        'IV crystalloid 30 mL/kg; high-flow O₂',
        'Monitor urine output (foley)',
      ] },
      { title: 'Shock / Source', lines: [
        'Vasopressor for fluid-refractory shock: norepinephrine',
        'Add vasopressin; consider hydrocortisone if refractory',
        'Source control: expedite delivery / evacuate retained products',
        'Involve ICU, OB, neonatology; monitor fetus',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'Fever 39.5 °C, warm peripheries, bounding then thready pulse',
        'Uterine tenderness, foul/purulent lochia',
        'Fetal tachycardia; maternal altered mental status',
      ] },
      { title: 'Labs', lines: [
        'Lactate 4.2 mmol/L; WBC 22 (or < 4); CRP ↑',
        'ABG: metabolic acidosis with respiratory compensation',
        'Cultures pending; consider DIC screen',
      ] },
    ],
  },

  obAspiration: {
    name: 'Aspiration (Mendelson)',
    description: 'Aspiration of gastric contents — the pregnant patient is high-risk (↓LOS tone, ↑intra-abdominal pressure).',
    capnoMorphology: 'obstructed',
    vent: { vte: 350, ppeak: 42, pplat: 24 },
    vitals: { HR: 118, SpO2: 84, NIBP_Sys: 104, NIBP_Dia: 64, ABP_Sys: 102, ABP_Dia: 62, EtCO2: 50, RR: 26, Temp: 37.2, CVP: 6 },
    aid: [
      { title: 'Immediate', lines: [
        'Head-down, left-lateral tilt; suction oropharynx',
        '100% O₂; secure airway with cuffed ETT; suction trachea BEFORE PPV',
        'Apply cricoid pressure until airway secured',
        'Do NOT lavage; bronchoscopy only for particulate obstruction',
      ] },
      { title: 'Support', lines: [
        'Ventilate with PEEP; treat bronchospasm (albuterol)',
        'Antibiotics only if evidence of infection (not prophylactic)',
        'Steroids NOT routinely recommended',
        'ICU for ongoing respiratory support if severe',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'Gastric contents in oropharynx/airway',
        'Wheeze, crackles; falling SpO₂; rising airway pressures',
      ] },
      { title: 'ABG / CXR', lines: [
        'ABG: hypoxaemia (PaO₂ 55) ± respiratory acidosis',
        'CXR: patchy infiltrates, classically right lower/middle lobe',
        '(may lag several hours behind clinical picture)',
      ] },
    ],
  },

  obFailedIntubation: {
    name: 'Failed Obstetric Intubation',
    description: 'Cannot intubate at C-section GA — rapid desaturation from reduced FRC and increased O₂ demand.',
    vitals: { HR: 125, SpO2: 78, NIBP_Sys: 138, NIBP_Dia: 84, ABP_Sys: 136, ABP_Dia: 82, EtCO2: 18, RR: 0, Temp: 36.9, CVP: 6 },
    aid: [
      { title: 'Declare & Optimise', lines: [
        'Declare failed intubation; call for help',
        'Maintain oxygenation: face-mask ± 2 hands, apnoeic O₂',
        'Optimise: head-up/ramped, external laryngeal manipulation',
        'Maximum 2 (+1 expert) intubation attempts',
        'Maintain cricoid pressure (release if impeding ventilation)',
      ] },
      { title: 'Rescue / Decide', lines: [
        'Insert 2nd-generation supraglottic airway (e.g. i-gel)',
        'If oxygenating: WAKE vs PROCEED decision (maternal/fetal urgency)',
        'CICO → front-of-neck access (scalpel-bougie cricothyroidotomy)',
        'Left uterine displacement throughout; ready to deliver',
      ] },
    ],
    investigations: [
      { title: 'Airway / Monitors', lines: [
        'Poor laryngoscopic view; oedematous, friable airway (pregnancy)',
        'SpO₂ 78% and falling despite pre-oxygenation',
        'Capnography flat — confirm ventilation route',
      ] },
    ],
  },

  obCordProlapse: {
    name: 'Umbilical Cord Prolapse',
    description: 'Cord presents below the fetus — fetal emergency; maternal vitals often near-normal.',
    vitals: { HR: 96, SpO2: 98, NIBP_Sys: 118, NIBP_Dia: 72, ABP_Sys: 116, ABP_Dia: 70, EtCO2: 34, RR: 16, Temp: 36.8, CVP: 5 },
    aid: [
      { title: 'Relieve Cord Compression', lines: [
        'Call for help; declare category-1 emergency',
        'Elevate the presenting part (manual, keep hand in vagina)',
        'Knee-chest or steep Trendelenburg / left lateral position',
        'Fill bladder (500–700 mL) to lift presenting part',
        'Stop oxytocin; consider tocolysis (terbutaline)',
      ] },
      { title: 'Delivery', lines: [
        'Expedite delivery — usually emergency Caesarean',
        'Anaesthesia: GA vs rapid neuraxial by urgency & fetal status',
        'Minimise handling of an exposed, cooling cord',
        'Continuous fetal monitoring; neonatal team present',
      ] },
    ],
    investigations: [
      { title: 'Findings', lines: [
        'Palpable/visible pulsating cord below presenting part',
        'Fetal bradycardia / variable decelerations on CTG',
        'Recent ROM, especially with unengaged/abnormal lie',
        '(Maternal vitals typically stable)',
      ] },
    ],
  },

  obPlacentalAbruption: {
    name: 'Placental Abruption',
    description: 'Premature placental separation — painful bleeding (may be concealed), uterine tetany, fetal distress, DIC.',
    vitals: { HR: 126, SpO2: 96, NIBP_Sys: 90, NIBP_Dia: 54, ABP_Sys: 88, ABP_Dia: 52, EtCO2: 30, RR: 24, Temp: 36.7, CVP: 2 },
    aid: [
      { title: 'Resuscitate', lines: [
        'Call for help (OB, anaesthesia, blood bank); activate MTP',
        '100% O₂; large-bore IV ×2; crystalloid then blood products',
        'Left uterine displacement; continuous fetal monitoring',
        'Blood loss is often UNDER-estimated (concealed)',
      ] },
      { title: 'Deliver & Treat DIC', lines: [
        'Expedite delivery (usually emergency Caesarean if fetal/maternal compromise)',
        'Anticipate and treat DIC: FFP, cryoprecipitate, platelets, fibrinogen',
        'Tranexamic acid 1 g IV',
        'Serial fibrinogen/TEG to guide replacement',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'Tense, tender, "woody-hard" uterus; frequent contractions',
        'Dark vaginal blood (or none if concealed)',
        'Fetal distress / bradycardia; maternal tachycardia, hypotension',
      ] },
      { title: 'Labs / US', lines: [
        'Fibrinogen 120 mg/dL and falling; platelets ↓; D-dimer ↑ (DIC)',
        'Hgb falling; Kleihauer–Betke if Rh-negative',
        'US may show retroplacental clot (poor sensitivity — clinical dx)',
      ] },
    ],
  },

  obPlacentaAccreta: {
    name: 'Placenta Accreta Spectrum',
    description: 'Abnormal placental invasion — torrential haemorrhage at attempted separation/delivery.',
    vitals: { HR: 135, SpO2: 94, NIBP_Sys: 76, NIBP_Dia: 44, ABP_Sys: 74, ABP_Dia: 42, EtCO2: 28, RR: 26, Temp: 35.9, CVP: 1 },
    aid: [
      { title: 'Massive Haemorrhage', lines: [
        'Activate MTP; multidisciplinary team (OB, anaesthesia, urology, IR)',
        'Large-bore access; arterial line; consider central access',
        'Rapid infuser + cell saver; warm all fluids',
        'Transfuse early 1:1:1; TXA 1 g IV',
      ] },
      { title: 'Surgical / Definitive', lines: [
        'Usually planned Caesarean-hysterectomy; leave placenta in situ',
        'Consider prophylactic ureteric stents, IR balloon occlusion',
        'General or neuraxial → often convert to GA for prolonged surgery',
        'Vigilant for coagulopathy, hypothermia, hypocalcaemia',
      ] },
    ],
    investigations: [
      { title: 'Imaging', lines: [
        'US: placental lacunae ("Swiss cheese"), loss of clear zone,',
        'increased vascularity, bladder-wall interruption',
        'MRI for posterior placenta / depth of invasion',
      ] },
      { title: 'Labs', lines: [
        'Hgb 6 and falling; fibrinogen low; iCa 0.8 (citrate)',
        'Lactate, base deficit rising; type & cross ≥ 4–6 units ready',
      ] },
    ],
  },

  obPulmonaryEdema: {
    name: 'Peripartum Pulmonary Edema',
    description: 'Pulmonary oedema from tocolytics (β-agonists), pre-eclampsia, fluid overload, or cardiac disease.',
    vitals: { HR: 122, SpO2: 86, NIBP_Sys: 162, NIBP_Dia: 96, ABP_Sys: 160, ABP_Dia: 94, EtCO2: 32, RR: 30, Temp: 37.0, CVP: 12 },
    aid: [
      { title: 'Immediate', lines: [
        'Sit patient up; 100% O₂; consider CPAP/NIV',
        'STOP tocolytics and stop/limit IV fluids',
        'Furosemide 20–40 mg IV; monitor urine output',
        'If hypertensive: reduce afterload (labetalol, hydralazine, GTN)',
      ] },
      { title: 'Ongoing', lines: [
        'Continue MgSO₄ if pre-eclamptic (but review fluid balance)',
        'Echo to assess LV function / valvular disease / PPCM',
        'Escalate to ICU if worsening hypoxaemia → intubate',
        'Treat underlying cause; cautious diuresis',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'Dyspnoea, orthopnoea; bilateral fine crackles; frothy sputum',
        'SpO₂ 86% on air; tachypnoea; raised JVP',
      ] },
      { title: 'CXR / Labs', lines: [
        'CXR: bilateral perihilar "bat-wing" infiltrates, Kerley B lines',
        'BNP elevated; ABG hypoxaemia; troponin if ischaemia suspected',
        'Echo: LV function, diastolic dysfunction, valvulopathy',
      ] },
    ],
  },

  obVAE: {
    name: 'Venous Air Embolism (C-section)',
    description: 'Air entrained via open uterine sinuses (esp. exteriorised uterus) — sudden EtCO₂ drop and hypotension.',
    // Flood field, 100% O₂ (stop N₂O), left-lateral/Durant, support with fluids + pressors.
    correctInterventions: ['nonRebreather', 'ivFluid', 'norepinephrine', 'epinephrine'],
    vitals: { HR: 120, SpO2: 90, NIBP_Sys: 84, NIBP_Dia: 50, ABP_Sys: 82, ABP_Dia: 48, EtCO2: 18, RR: 24, Temp: 36.8, CVP: 10 },
    aid: [
      { title: 'Immediate', lines: [
        'Tell surgeon: FLOOD the field with saline; lower the surgical site',
        'Prevent further entrainment; reposition uterus into abdomen',
        '100% O₂ (stop N₂O); support BP with fluids + vasopressor',
        'Left lateral / Durant position; aspirate air from CVL if present',
      ] },
      { title: 'Support', lines: [
        'If awake under neuraxial: reassure; watch for dyspnoea/desat',
        'CPR if arrest; consider hyperbaric O₂ for large/paradoxical embolism',
        'TEE/precordial Doppler confirms and monitors air',
      ] },
    ],
    investigations: [
      { title: 'Findings', lines: [
        'Sudden fall in EtCO₂ 18 mmHg; hypotension, hypoxaemia',
        'Awake patient: dyspnoea, chest pain, cough',
        '"Mill-wheel" murmur on auscultation (large embolism)',
      ] },
      { title: 'TEE / Doppler', lines: [
        'Air visible in right heart on TEE',
        'Precordial Doppler: characteristic change with air entrainment',
      ] },
    ],
  },
};

/**
 * Cognitive-aid + investigation cards attached to the EXISTING core obstetric
 * scenarios (defined in scenarios.js) so the whole OB/GYN tab supports the
 * push-to-monitor sub-buttons.
 */
export const OB_AID_ATTACH = {
  obgynCardiacArrest: {
    aid: [
      { title: 'Maternal BLS/ACLS', lines: [
        'Standard high-quality CPR: 100–120/min, depth ≥ 5 cm',
        'Hands slightly higher on sternum (gravid uterus)',
        'Defibrillation energy UNCHANGED; remove fetal monitors before shock',
        'Epinephrine 1 mg IV q3–5 min; secure airway early (aspiration risk)',
      ] },
      { title: 'OB Modifications', lines: [
        'Manual LEFT UTERINE DISPLACEMENT throughout (relieve aortocaval compression)',
        'Perimortem Caesarean if no ROSC by 4 min — deliver by 5 min',
        'Improves maternal resuscitation; do NOT move patient to do it',
        'IV access ABOVE the diaphragm',
      ] },
      { title: 'Reverse Causes (BEAU-CHOPS)', lines: [
        'Bleeding/DIC · Embolism (PE/AFE) · Anaesthetic complications',
        'Uterine atony · Cardiac disease · Hypertension (eclampsia)',
        'Other (H\'s & T\'s) · Placental abruption/praevia · Sepsis',
      ] },
    ],
    investigations: [
      { title: 'During Resuscitation', lines: [
        'Rhythm check every 2 min (VF/VT shockable vs PEA/asystole)',
        'EtCO₂ > 10 mmHg = adequate CPR; abrupt rise = ROSC',
        'POCUS between cycles: tamponade, RV strain (PE/AFE), volume',
      ] },
      { title: 'Labs', lines: [
        'ABG: severe mixed acidosis; K⁺, iCa, glucose',
        'Hgb / fibrinogen (haemorrhage, DIC); lactate',
      ] },
    ],
  },
  obgynPeripartumCM: {
    aid: [
      { title: 'Stabilise', lines: [
        '100% O₂; sit up; CPAP/NIV → intubate if deteriorating',
        'Cautious diuresis: furosemide (risk of flash pulmonary oedema)',
        'Afterload reduction: hydralazine/nitrates (avoid ACEi if pregnant)',
        'Inotrope for cardiogenic shock: dobutamine or epinephrine/milrinone',
      ] },
      { title: 'Definitive', lines: [
        'Beta-blocker once compensated (carvedilol)',
        'Anticoagulation (high thromboembolic risk with low EF)',
        'Bromocriptine may halt progression (specialist decision)',
        'ECMO / mechanical support for refractory shock; deliver if antepartum',
      ] },
    ],
    investigations: [
      { title: 'Echo', lines: [
        'Dilated LV, global hypokinesis; EF < 45% (often < 30%)',
        'Functional mitral regurgitation; ± mural thrombus',
        'Idiopathic — last month of pregnancy to 5 months postpartum',
      ] },
      { title: 'Labs / CXR', lines: [
        'BNP markedly elevated; troponin mildly ↑',
        'CXR: cardiomegaly + pulmonary oedema',
        'ABG hypoxaemia; rule out ischaemia, PE, sepsis',
      ] },
    ],
  },
  obgynEclampsia: {
    aid: [
      { title: 'Seizure / Airway', lines: [
        'Protect airway; left lateral; 100% O₂; suction',
        'MgSO₄ 4–6 g IV over 15–20 min, then 1–2 g/hr',
        'Recurrent seizure: further MgSO₄ 2 g IV (or benzodiazepine)',
        'Watch for magnesium toxicity → calcium gluconate ready',
      ] },
      { title: 'BP & Delivery', lines: [
        'Control BP (target SBP < 160) to prevent stroke:',
        'labetalol 5–10 mg IV or hydralazine 2–5 mg IV',
        'Left uterine displacement; continuous fetal monitoring',
        'Deliver once maternal condition stabilised',
      ] },
    ],
    investigations: [
      { title: 'Physical Exam', lines: [
        'Tonic-clonic seizure then post-ictal; hyperreflexia, clonus',
        'Severe hypertension, headache, visual disturbance, oedema',
        'Post-ictal hypoventilation and hypoxaemia',
      ] },
      { title: 'Labs', lines: [
        'Proteinuria; uric acid ↑; consider HELLP (LFTs, platelets, LDH)',
        'ABG (post-ictal CO₂ retention); coagulation panel',
      ] },
    ],
  },
  obgynHemorrhagicShock: {
    aid: [
      { title: 'Resuscitate', lines: [
        'Activate MTP; call OB/anaesthesia/nursing help',
        'Large-bore IV ×2; rapid infuser + cell saver; warm fluids',
        'Transfuse 1:1:1 (PRBC:FFP:platelets); TXA 1 g IV',
        'Vasopressor bolus to temporise; arterial line + foley',
      ] },
      { title: 'Identify Cause (4 T\'s)', lines: [
        'Tone: uterine atony → uterotonic ladder',
        'Trauma: lacerations, rupture, inversion',
        'Tissue: retained products / placenta',
        'Thrombin: coagulopathy / DIC → factor replacement',
      ] },
    ],
    investigations: [
      { title: 'Labs / TEG', lines: [
        'Hgb 6; platelets low; fibrinogen < 150 (target > 200 peripartum)',
        'INR/PTT ↑; lactate ↑; base deficit; iCa low',
        'TEG/ROTEM to target component therapy',
      ] },
    ],
  },
  obgynHighSpinal: {
    aid: [
      { title: 'Support ABC', lines: [
        '100% O₂; support ventilation, intubate if apnoeic (may last hours)',
        'Reassure conscious patient; midazolam for amnesia',
        'Severe brady/hypotension: epinephrine 10–100 mcg IV, escalate',
        'Rapid IV fluids + legs up; phenylephrine unlikely effective',
      ] },
      { title: 'Obstetric', lines: [
        'Left uterine displacement; call OB and neonatology',
        'Prepare for emergent / perimortem Caesarean',
        'If LAST possible: 20% lipid emulsion',
      ] },
    ],
    investigations: [
      { title: 'Exam', lines: [
        'Ascending sensory/motor level; weak hand grip; dysphonia',
        'Bradycardia + profound hypotension (sympathectomy) → apnoea',
      ] },
    ],
  },
  obgynSCAD: {
    aid: [
      { title: 'Treat as ACS', lines: [
        'O₂ to keep SpO₂ ≥ 95%; 12-lead ECG; defib pads on',
        'Aspirin; discuss anticoagulation risk with team',
        'AVOID thrombolytics (haemorrhage / dissection extension risk)',
        'Treat pain; manage pulmonary oedema (furosemide)',
      ] },
      { title: 'Escalate', lines: [
        'Urgent Cardiology — PCI vs conservative for SCAD',
        'Arterial line; consider IABP/ECMO if cardiogenic shock',
        'Left uterine displacement; plan delivery with team',
      ] },
    ],
    investigations: [
      { title: 'ECG / Labs / Echo', lines: [
        'ST elevation (LAD territory most common in peripartum SCAD)',
        'Troponin elevated; echo shows regional wall-motion abnormality',
        'Coronary angiography is diagnostic (intramural haematoma)',
      ] },
    ],
  },
};
